import { useQuery } from '@tanstack/react-query';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { dcrEntryQueryOptions } from '../api/billing.api';
import type { DcrEntryLine } from '../types/billing.types';
import { DcrEntryLinesTable } from './DcrEntryLinesTable';
import { DcrEntrySummary } from './DcrEntrySummary';
import { DetailLinesSkeleton } from './DetailLinesSkeleton';

const LINES_SKELETON_COLUMN_COUNT = 6;
const LINE_KIND_ORDER: DcrEntryLine['lineKind'][] = ['specimen', 'promo', 'contact'];

interface DcrEntryDetailsModalProps {
  recordId: number | null;
  onClose: () => void;
}

function DcrEntryLineSections({ lines }: { lines: DcrEntryLine[] }) {
  if (lines.length === 0) {
    return (
      <EmptyState
        title="No items on this entry"
        description="This work type has no specimens, promotion items or contacts."
        size="compact"
      />
    );
  }

  return LINE_KIND_ORDER.map((lineKind) => {
    const linesOfKind = lines.filter((line) => line.lineKind === lineKind);
    if (linesOfKind.length === 0) return null;
    return <DcrEntryLinesTable key={lineKind} lineKind={lineKind} lines={linesOfKind} />;
  });
}

export function DcrEntryDetailsModal({ recordId, onClose }: DcrEntryDetailsModalProps) {
  const { data, isLoading, error, refetch } = useQuery(dcrEntryQueryOptions(recordId));

  return (
    <Dialog
      open={recordId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-auto sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>DCR Details</DialogTitle>
        </DialogHeader>

        {error ? (
          <ErrorState
            error={error}
            fallbackTitle="Could not load this entry"
            onRetry={() => void refetch()}
            size="compact"
          />
        ) : (
          <div className="flex flex-col gap-6">
            <DcrEntrySummary entry={data} />
            {isLoading || !data ? (
              <DetailLinesSkeleton columnCount={LINES_SKELETON_COLUMN_COUNT} />
            ) : (
              <DcrEntryLineSections lines={data.lines} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
